import { createContext, useContext, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const Ctx = createContext(null);

/** Fournit une garde globale contre la perte de modifications non enregistrées. */
export function GuardProvider({ children }) {
  const navigate = useNavigate();
  const guardRef = useRef(null); // { isDirty:()=>bool, save:async }
  const [pending, setPending] = useState(null); // { to, action }

  const run = (p) => { guardRef.current = null; setPending(null); if (p.action) p.action(); else navigate(p.to); };
  const attempt = (to, action) => {
    const g = guardRef.current;
    if (g && g.isDirty()) setPending({ to, action });
    else run({ to, action });
  };
  const register = (isDirty, save) => { guardRef.current = { isDirty, save }; return () => { guardRef.current = null; }; };
  const saveAndGo = async () => { try { await guardRef.current?.save?.(); } catch (e) { /* noop */ } run(pending); };

  return (
    <Ctx.Provider value={{ attempt, register }}>
      {children}
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Modifications non enregistrées</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Des modifications ont été faites. Faut-il les enregistrer ?</p>
          <DialogFooter>
            <button onClick={() => setPending(null)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
            <button data-testid="guard-discard" onClick={() => run(pending)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">Non</button>
            <button data-testid="guard-save" onClick={saveAndGo} className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium">Oui, enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export const useGuard = () => useContext(Ctx);

/** À appeler dans un écran d'édition : enregistre l'état "sale" + la fonction de sauvegarde. */
export function useUnsavedGuard(isDirty, save) {
  const { register } = useGuard();
  const dRef = useRef(isDirty); dRef.current = isDirty;
  const sRef = useRef(save); sRef.current = save;
  useEffect(() => register(() => dRef.current, () => sRef.current()), []); // eslint-disable-line
  useEffect(() => {
    const h = (e) => { if (dRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);
}
